import { useEffect, useState } from 'react';

// Redux/Hooks/APIs/Utils
import { getTranslateByKey, isNullUndefined } from '@/main/services/utils';
import { RateGridRequest } from '@/main/models/request/rateRequest';
import { RateResponse } from '@/main/models/response/rateResponse';
import { rateApi } from '@/main/api';

// Components
import { RowHeaderTextCenter, RowHeaderTextEnd } from '@/main/components/shared/styles';
import RowRate from './rowRate';
import RateSidebar from '../rateSidebar/rateSidebar';
import BoxGrid from '@/main/components/shared/boxGrid/boxGrid';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import LoadingComponent from '@/main/components/shared/loading/loadingComponent';

// Styles
import * as S from '../styles';

interface IRateSection {
  guidCompany: string | null | undefined;
  guidBodyShop: string | null | undefined;
}

function RateSection({ guidCompany, guidBodyShop }: Readonly<IRateSection>) {
  // #region states
  const [dataGrid, setDataGrid] = useState<RateResponse[]>([]);
  const [isLoadingRate, setIsLoadingRate] = useState<boolean>(false);

  const [openModalRate, setOpenModalRate] = useState<boolean>(false);
  // #endregion

  // #region functions

  function getRates() {
    if (isNullUndefined(guidCompany)) return;
    const request: RateGridRequest = {
      guidCompany
    };
    setIsLoadingRate(true);
    rateApi
      .searchGrid(request)
      .then(res => {
        const result = res.data.result;
        setDataGrid(result);
      })
      .finally(() => {
        setIsLoadingRate(false);
      });
  }

  // #endregion

  // #region handlers
  function handleNewRate() {
    setOpenModalRate(true);
  }

  function closeModalRate(refresh: boolean = false) {
    setOpenModalRate(false);
    if (refresh) {
      setDataGrid([]);
      getRates();
    }
  }

  function handleLocalRemove() {
    setDataGrid([]);
    getRates();
  }
  // #endregion

  // #region effects
  useEffect(() => {
    if (!guidCompany) return;
    getRates();
  }, [guidCompany]);
  // #endregion

  // #region render constants
  const rowHeaders = [
    getTranslateByKey('information.insurer'),
    getTranslateByKey('information.make'),
    getTranslateByKey('information.ageStart'),
    getTranslateByKey('information.ageEnd'),
    getTranslateByKey('information.affectedType'),
    getTranslateByKey('information.rateLabour'),
    getTranslateByKey('information.percentDiscountParts'),
    getTranslateByKey('information.observation'),
    getTranslateByKey('information.dateTimeUser'),
    getTranslateByKey('information.status'),
    <RowHeaderTextCenter>
      <span>{getTranslateByKey('information.actions')}</span>
    </RowHeaderTextCenter>
  ];

  const ID_SCROLLABLE_TARGET = 'id-scrollable-rate-container';
  // #endregion

  return (
    <>
      <S.ContainerSection>
        <S.ContainerTitle>
          <S.HeaderText>{getTranslateByKey('information.rate')}</S.HeaderText>
          <SecondaryButton
            id="id-rate-btn-addRate"
            onClick={() => handleNewRate()}
            style={{ padding: '.2rem .8rem', margin: '.5rem 0 0 .8rem' }}>
            {getTranslateByKey('information.addRate')}
          </SecondaryButton>
        </S.ContainerTitle>

        <S.ContainerBoxGrid id={ID_SCROLLABLE_TARGET}>
          {isLoadingRate ? (
            <LoadingComponent />
          ) : (
            dataGrid?.length > 0 && (
              <BoxGrid rowHeaders={rowHeaders}>
                {dataGrid.map((rate, indexRate) => (
                  <RowRate
                    key={rate.guidRate}
                    rate={rate}
                    index={indexRate}
                    onRemove={handleLocalRemove}
                  />
                ))}
              </BoxGrid>
            )
          )}
        </S.ContainerBoxGrid>
      </S.ContainerSection>

      <RateSidebar
        open={openModalRate}
        closeSidebar={closeModalRate}
        guidCompany={guidCompany}
        guidBodyShop={guidBodyShop}
      />
    </>
  );
}

export default RateSection;
